import { useState } from 'react';
import { del, get, patch, post } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { photoToDataUrl } from '../lib/image';
import { formatDateTime, formatTime, zone } from '../../shared/format';
import { STALE_SHIFT_MS, sinceWords, wallClock } from '../../shared/timeclock';
import { Badge, Button, Confirm, Empty, Field, Sheet, Spinner, Tabs, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { EmployeeBadge } from '../ui/brand';

const ROLES = [
  { id: 'owner', label: 'Owner', rank: 100 },
  { id: 'district_manager', label: 'District Manager', rank: 80 },
  { id: 'manager', label: 'Manager', rank: 60 },
  { id: 'sales', label: 'Sales', rank: 20 },
  { id: 'technician', label: 'Technician', rank: 20 },
];
const RANK: Record<string, number> = Object.fromEntries(ROLES.map((r) => [r.id, r.rank]));

const ROLE_HELP: Record<string, string> = {
  owner: 'Everything, in every store.',
  district_manager: 'Everything in every store, except changing settings and roles.',
  manager: 'Runs one store: refunds, discounts, prices, stock counts, staff, drawer reviews.',
  sales: 'Sells, takes customers, opens and closes the drawer. Can’t see costs or change prices.',
  technician: 'Repairs, including seeing passcodes, plus selling. No discounts without a manager.',
};

/** The store's time zone (Settings). */
function useZone(): string {
  const { me } = useSession();
  return me?.settings?.timezone || zone.current;
}

export function EmployeesScreen() {
  const { me } = useSession();
  const tz = useZone();
  const list = useLoad(() => get('/api/employees?inactive=1'), []);
  // Hours reload when someone is clocked out from their sheet.
  const [hoursKey, setHoursKey] = useState(0);
  const [editing, setEditing] = useState<any>(null);
  const [adding, setAdding] = useState(false);
  const [pin, setPin] = useState<{ name: string; pin: string } | null>(null);
  const toast = useToast();
  const locName = (id: string) => me?.locations.find((l) => l.id === id)?.name ?? '';
  return (
    <div className="page">
      <div className="page-head">
        <h1>Employees</h1>
        <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add employee</Button>
      </div>
      {list.loading && !list.data ? <Spinner /> : null}
      {list.data?.length ? (
        <div className="list">
          {list.data.map((e: any) => (
            <button key={e.id} type="button" className="list-row" onClick={() => setEditing(e)} style={{ opacity: e.active ? 1 : 0.55 }}>
              <EmployeeBadge person={e} className="mini" />
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{e.name}</span>
                <span className="s" style={{ display: 'block' }}>{e.all_locations ? 'All stores' : e.location_ids.map(locName).filter(Boolean).join(', ')}{e.email ? ` · ${e.email}` : ''}</span>
              </span>
              {e.clocked_in_at ? <Badge tone={Date.now() - e.clocked_in_at > STALE_SHIFT_MS ? 'amber' : 'green'} icon="clock">In since {sinceWords(e.clocked_in_at, Date.now(), tz)}</Badge> : null}
              {e.active && !e.has_pin ? <Badge tone="amber">No code yet</Badge> : null}
              <Badge tone={e.role_id === 'owner' ? 'blue' : 'plain'}>{e.role}</Badge>
              {!e.active ? <Badge tone="red">Turned off</Badge> : null}
            </button>
          ))}
        </div>
      ) : list.data ? <Empty icon="users" title="No employees yet." /> : null}
      <HoursCard key={hoursKey} onChanged={() => list.reload()} />
      {adding ? <EmployeeSheet onClose={() => setAdding(false)} onDone={(r) => { setAdding(false); list.reload(); if (r?.pin) setPin({ name: r.employee.first_name, pin: r.pin }); }} /> : null}
      {editing ? <EmployeeSheet employee={editing} onBadge={() => list.reload()} onClockedOut={() => { setEditing(null); list.reload(); setHoursKey((k) => k + 1); }} onClose={() => setEditing(null)} onDone={(r) => { setEditing(null); list.reload(); setHoursKey((k) => k + 1); if (r?.pin) setPin({ name: editing.first_name, pin: r.pin }); else if (r && 'pin' in r) toast(`${editing.first_name} makes a new code the next time they clock in.`, 'good'); }} /> : null}
      {pin ? (
        <Sheet title={`${pin.name}'s PIN`} onClose={() => setPin(null)} footer={<Button variant="primary" onClick={() => setPin(null)}>I wrote it down</Button>}>
          <p style={{ fontSize: 48, lineHeight: '56px', fontWeight: 700, letterSpacing: '0.2em', textAlign: 'center' }} className="num">{pin.pin}</p>
          <div className="banner warn"><Icon.lock /><div>Give this PIN to {pin.name} privately. It won't be shown again. They can change it later.</div></div>
        </Sheet>
      ) : null}
    </div>
  );
}

function EmployeeSheet({ employee, onClose, onDone, onBadge, onClockedOut }: { employee?: any; onClose: () => void; onDone: (r?: any) => void; onBadge?: () => void; onClockedOut?: () => void }) {
  const { me } = useSession();
  const tz = useZone();
  const { run, busy } = useAction();
  const [clockingOut, setClockingOut] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const myRank = RANK[me?.employee.role_id ?? 'sales'] ?? 0;
  const roles = ROLES.filter((r) => me?.employee.role_id === 'owner' || r.rank < myRank);
  const [f, setF] = useState({
    firstName: employee?.first_name ?? '',
    lastName: employee?.last_name ?? '',
    roleId: employee?.role_id ?? 'sales',
    homeLocationId: employee?.home_location_id ?? me?.location?.id ?? me?.locations[0]?.id ?? '',
    locationIds: (employee?.location_ids ?? []) as string[],
    email: employee?.email ?? '',
    password: '',
    pin: '',
    active: employee?.active ?? true,
  });
  const allStores = f.roleId === 'owner' || f.roleId === 'district_manager';
  const needsEmail = ['owner', 'district_manager', 'manager'].includes(f.roleId);
  const save = async () => {
    const body: any = {
      firstName: f.firstName, lastName: f.lastName, roleId: f.roleId, homeLocationId: allStores ? f.homeLocationId || null : f.homeLocationId,
      locationIds: [...new Set([f.homeLocationId, ...f.locationIds].filter(Boolean))], email: f.email || null,
    };
    if (f.password) body.password = f.password;
    if (f.pin) body.pin = f.pin;
    if (employee) body.active = f.active;
    const r = await run(() => (employee ? patch(`/api/employees/${employee.id}`, body) : post('/api/employees', body)), { success: employee ? 'Saved.' : 'Employee added.' });
    if (r) onDone(r);
  };
  const resetPin = async () => {
    const r = await run(() => post(`/api/employees/${employee.id}/reset-pin`));
    if (r) onDone(r);
  };
  const remove = async () => {
    const r = await run(() => del(`/api/employees/${employee.id}`), { success: `${employee.first_name} deleted.` });
    if (r) onDone(r);
  };
  return (
    <Sheet
      title={employee ? `Edit ${employee.name}` : 'Add an employee'}
      onClose={onClose}
      wide
      footer={
        <>
          {employee ? <Button icon="refresh" onClick={resetPin} busy={busy}>Forgot code</Button> : null}
          {employee?.clocked_in_at && employee.id !== me?.employee.id ? <Button icon="clock" onClick={() => setClockingOut(true)}>Clock out…</Button> : null}
          {employee && me?.employee.role_id === 'owner' && employee.id !== me?.employee.id ? <Button variant="danger" icon="trash" onClick={() => setDeleting(true)}>Delete</Button> : null}
          <span className="grow" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={save} disabled={!f.firstName.trim() || (!allStores && !f.homeLocationId) || (!!f.pin && !/^\d{4,6}$/.test(f.pin))}>{employee ? 'Save' : 'Add employee'}</Button>
        </>
      }
    >
      {employee ? <BadgeField employee={employee} onChange={onBadge} /> : null}
      <div className="form-grid">
        <Field label="First name"><input className="input" autoFocus value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} /></Field>
        <Field label="Last name"><input className="input" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} /></Field>
        <Field label="Role" className="full">
          <div className="chips">
            {roles.map((r) => (
              <button key={r.id} type="button" className="chip" aria-pressed={f.roleId === r.id} onClick={() => setF({ ...f, roleId: r.id })}>{r.label}</button>
            ))}
          </div>
          <span className="hint">{ROLE_HELP[f.roleId]}</span>
        </Field>
        {!allStores ? (
          <>
            <Field label="Home store">
              <select className="select" value={f.homeLocationId} onChange={(e) => setF({ ...f, homeLocationId: e.target.value })}>
                {me?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Can also work at">
              <div className="chips">
                {me?.locations.filter((l) => l.id !== f.homeLocationId).map((l) => (
                  <button key={l.id} type="button" className="chip" aria-pressed={f.locationIds.includes(l.id)} onClick={() => setF({ ...f, locationIds: f.locationIds.includes(l.id) ? f.locationIds.filter((x) => x !== l.id) : [...f.locationIds, l.id] })}>{l.name}</button>
                ))}
              </div>
            </Field>
          </>
        ) : <p className="full small muted">Works in every store.</p>}
        {needsEmail ? (
          <>
            <Field label="Email" hint="to sign in from a computer"><input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label={employee?.has_password ? 'New password (optional)' : 'Password'} hint="10+ characters"><input className="input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" /></Field>
          </>
        ) : null}
        <Field label={employee?.has_pin ? 'New code (PIN)' : 'Code (PIN)'} hint={employee ? 'blank: unchanged · 4 to 6 digits' : '4 to 6 digits, or blank: they make one at their first clock-in'}>
          <input className="input num" inputMode="numeric" autoComplete="off" value={f.pin} maxLength={6} placeholder="e.g. 4826" aria-label="Code (PIN)" onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
        </Field>
        {employee && employee.id !== me?.employee.id ? (
          <label className="check full"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Can sign in (turn off when someone leaves)</label>
        ) : null}
      </div>
      {employee?.clocked_in_at ? <p className="small muted">Clocked in since {sinceWords(employee.clocked_in_at, Date.now(), tz)}.</p> : null}
      {employee && !f.active && employee.clocked_in_at ? <p className="small strong">Turning {employee.first_name} off ends their shift at their last activity on a tablet.</p> : null}
      {!employee ? <p className="small muted">Type their code above, or leave it blank: they make their own 4-digit code the first time they tap their name to clock in.</p> : <p className="small muted">Type a new code above to change theirs, or "Forgot code" clears it so they make a new one at their next clock-in.</p>}
      {clockingOut ? (
        <PunchSheet
          punch={{ employee_id: employee.id, name: employee.name, clock_in_at: employee.clocked_in_at, clock_out_at: null, open: true, stale: Date.now() - employee.clocked_in_at > STALE_SHIFT_MS }}
          clockOut
          onClose={() => setClockingOut(false)}
          onDone={() => { setClockingOut(false); onClockedOut?.(); }}
        />
      ) : null}
      {deleting ? (
        <Confirm title={`Delete ${employee?.name}?`} confirmLabel="Delete" danger busy={busy} onClose={() => setDeleting(false)} onConfirm={remove}>Their past sales, repairs and history stay in the system. This is recorded in the audit log.</Confirm>
      ) : null}
    </Sheet>
  );
}

/**
 * Their badge: the card they see when they clock in and at the top right once they're in. Upload
 * their card (a picture, 2 wide by 3 tall), or leave it and STORE OS draws one with their name.
 */
function BadgeField({ employee, onChange }: { employee: any; onChange?: () => void }) {
  const { run, busy } = useAction();
  const toast = useToast();
  const [cur, setCur] = useState<any>(employee);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    let dataUrl: string;
    try {
      dataUrl = await photoToDataUrl(file, 1200, 0.88);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture couldn’t be read.', 'error');
      return;
    }
    const r = await run(() => post(`/api/employees/${employee.id}/badge`, { dataUrl }), { success: `${employee.first_name}'s badge is saved.` });
    if (r) { setCur(r); onChange?.(); }
  };
  const remove = async () => {
    const r = await run(() => del(`/api/employees/${employee.id}/badge`), { success: 'Badge picture removed. STORE OS draws one with their name.' });
    if (r) { setCur(r); onChange?.(); }
  };
  return (
    <div className="badge-field">
      <EmployeeBadge person={cur} className="pick" />
      <div className="stack-sm">
        <div className="strong">Badge</div>
        <p className="small muted">{cur.badge_url ? 'Their own badge. They see it when they clock in and at the top right of the screen.' : 'STORE OS drew this one with their name, job and store. Upload their own badge to use it instead.'}</p>
        <div className="row-wrap">
          <label className={`btn btn-secondary${busy ? ' disabled' : ''}`}>
            <Icon.badge />
            {cur.badge_url ? 'Change badge' : 'Upload badge'}
            <input type="file" accept="image/*" className="sr-only" disabled={busy} onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          {cur.badge_url ? <Button variant="ghost" busy={busy} onClick={remove}>Use the drawn one</Button> : null}
        </div>
      </div>
    </div>
  );
}

/** Hours from the time clock: this week or last week (for payroll), with shifts to fix. */
function HoursCard({ onChanged }: { onChanged: () => void }) {
  const tz = useZone();
  const [week, setWeek] = useState<'this' | 'last'>('this');
  const now = new Date();
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  const from = week === 'this' ? monday.getTime() : monday.getTime() - 7 * 86_400_000;
  const to = week === 'this' ? Date.now() : monday.getTime();
  const t = useLoad(() => get(`/api/timeclock?from=${from}&to=${to}`), [week]);
  const [open, setOpen] = useState<string | null>(null);
  const [fixing, setFixing] = useState<{ punch: any; clockOut: boolean } | null>(null);
  const shiftWords = (x: any) => {
    const start = `${formatDateTime(x.clock_in_at, tz)}`;
    if (!x.clock_out_at) return `${start} → ${x.stale ? 'no clock-out' : 'now'}`;
    const sameDay = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(x.clock_in_at)) === new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(x.clock_out_at));
    return `${start} → ${sameDay ? formatTime(x.clock_out_at, tz) : formatDateTime(x.clock_out_at, tz)}`;
  };
  return (
    <section className="card stack-sm" style={{ marginTop: 16 }}>
      <div className="spread">
        <h2>Hours</h2>
        <Tabs label="Week" value={week} onChange={setWeek} tabs={[{ id: 'this', label: 'This week' }, { id: 'last', label: 'Last week' }]} />
      </div>
      {!t.data ? <Spinner /> : t.data.people.length ? (
        <div className="list">
          {t.data.people.map((p: any) => (
            <div key={p.employee_id}>
              <button type="button" className="list-row" aria-expanded={open === p.employee_id} onClick={() => setOpen(open === p.employee_id ? null : p.employee_id)}>
                <span className="main-col"><span className="t" style={{ display: 'block' }}>{p.name}</span><span className="s" style={{ display: 'block' }}>{p.shifts} shift{p.shifts === 1 ? '' : 's'}{p.open ? ' · clocked in now' : ''}</span></span>
                {p.needs_fix ? <Badge tone="amber" icon="alert">Didn’t clock out — fix it</Badge> : null}
                <span className="strong num">{p.hours.toFixed(2)} h</span>
              </button>
              {open === p.employee_id ? t.data.punches.filter((x: any) => x.employee_id === p.employee_id).map((x: any) => (
                <div key={x.id} className="punch-row small">
                  <span className="grow">
                    {shiftWords(x)}
                    {x.stale ? null : x.note ? <span className="muted"> ({x.note})</span> : x.auto_closed ? <span className="muted"> (forgot to clock out)</span> : null}
                    {x.location_name && t.data.punches.some((y: any) => y.location_name !== x.location_name) ? <span className="muted"> · {x.location_name}</span> : null}
                  </span>
                  {x.stale ? <Badge tone="amber">Didn’t clock out — fix it</Badge> : null}
                  {x.can_fix ? (
                    x.open && !x.stale
                      ? <Button size="sm" onClick={() => setFixing({ punch: x, clockOut: true })}>Clock out</Button>
                      : <Button size="sm" variant={x.stale ? 'primary' : 'secondary'} onClick={() => setFixing({ punch: x, clockOut: false })}>{x.stale ? 'Fix it' : 'Fix'}</Button>
                  ) : null}
                  <span className="num nowrap">{x.stale ? '—' : `${x.hours.toFixed(2)} h`}</span>
                </div>
              )) : null}
            </div>
          ))}
        </div>
      ) : <p className="small muted">No hours yet. Staff clock in by tapping their name on the store tablet.</p>}
      {t.data?.people.some((p: any) => p.needs_fix) ? <p className="small muted">A shift with no clock-out stops counting after 16 hours. Put in when they left and it counts again.</p> : null}
      {fixing ? <PunchSheet punch={fixing.punch} clockOut={fixing.clockOut} onClose={() => setFixing(null)} onDone={() => { setFixing(null); t.reload(); onChanged(); }} /> : null}
    </section>
  );
}

/**
 * A manager clocks someone out (picking when they left) or fixes a shift's in and out times. The
 * times are store time; every change goes in the audit log.
 */
function PunchSheet({ punch, clockOut, onClose, onDone }: { punch: any; clockOut?: boolean; onClose: () => void; onDone: () => void }) {
  const tz = useZone();
  const toast = useToast();
  const { run, busy } = useAction();
  // A shift nobody clocked out of: start from their last activity on the tablet, when there was one.
  const guess = punch.clock_out_at ?? (punch.stale ? punch.last_seen_at ?? null : clockOut ? Date.now() : null);
  const [inAt, setInAt] = useState(wallClock(punch.clock_in_at, tz));
  const [outAt, setOutAt] = useState(guess ? wallClock(guess, tz) : '');
  const first = String(punch.name ?? '').split(' ')[0] || 'them';
  const startText = wallClock(punch.clock_in_at, tz);
  const save = async () => {
    const body: Record<string, string> = {};
    if (!clockOut && inAt !== startText) body.clockIn = inAt;
    if (outAt && outAt !== (punch.clock_out_at ? wallClock(punch.clock_out_at, tz) : '')) body.clockOut = outAt;
    if (!Object.keys(body).length) return onClose();
    const r = await run(() => (punch.id ? patch(`/api/timeclock/punches/${punch.id}`, body) : post('/api/timeclock/clock-out', { employeeId: punch.employee_id, at: body.clockOut })));
    if (!r) return;
    toast(punch.open && body.clockOut ? `${first} is clocked out (${r.hours.toFixed(2)} h).` : 'Shift fixed.', 'good');
    onDone();
  };
  const title = clockOut ? `Clock out ${punch.name}` : `Fix ${punch.name}’s shift`;
  return (
    <Sheet title={title} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={(clockOut || punch.stale) && !outAt} onClick={save}>{clockOut ? 'Clock out' : 'Save'}</Button></>}>
      <p className="muted">Clocked in {sinceWords(punch.clock_in_at, Date.now(), tz)}{punch.location_name ? ` at ${punch.location_name}` : ''}.{punch.stale ? ' No clock-out yet, so this shift isn’t counted.' : ''}</p>
      <div className="form-grid">
        {!clockOut ? <Field label="Clocked in"><input className="input" type="datetime-local" value={inAt} max={wallClock(Date.now(), tz)} onChange={(e) => setInAt(e.target.value)} /></Field> : null}
        <Field label={clockOut ? 'When they left' : 'Clocked out'} hint={punch.open && !punch.stale && !clockOut ? 'empty: still here' : undefined}>
          <input className="input" type="datetime-local" value={outAt} min={inAt} max={wallClock(Date.now(), tz)} onChange={(e) => setOutAt(e.target.value)} />
        </Field>
      </div>
      {punch.stale && punch.last_seen_at ? <p className="small muted">Last seen on a tablet {sinceWords(punch.last_seen_at, Date.now(), tz)}.</p> : null}
      <p className="small muted">Store time. Saved in the audit log with your name.</p>
    </Sheet>
  );
}
